import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PostcodeInput from "@/components/PostcodeInput";

export default function NewBrief() {
  const nav = useNavigate();
  const [f, setF] = useState({
    title: "", description: "", location: "", postcode: "",
    approx_items: "", move_by_date: "", budget: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...f, budget: f.budget ? parseFloat(f.budget) : null };
      const { data } = await api.post("/briefs", payload);
      toast.success("Clearance request posted");
      nav(`/briefs/${data.brief_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">Post a clearance request</div>
      <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">New clearance request</h1>
      <p className="mt-3 text-muted-foreground">Give removal firms enough detail to quote accurately. Free to post.</p>

      <form onSubmit={submit} className="mt-8 border border-border bg-white p-8 space-y-6">
        <Field label="Title *">
          <Input required value={f.title} onChange={set("title")} placeholder="Shoreditch tech-firm — 4th floor clearance" data-testid="brief-title" className="rounded-none h-12" />
        </Field>
        <Field label="Description *">
          <Textarea required rows={5} value={f.description} onChange={set("description")} placeholder="What's the space, access constraints, timing, anything special…" data-testid="brief-description" className="rounded-none" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Location (town/city) *">
            <Input required value={f.location} onChange={set("location")} placeholder="London" data-testid="brief-location" className="rounded-none h-12" />
          </Field>
          <Field label="Postcode">
            <PostcodeInput
              value={f.postcode}
              onChange={(v) => set("postcode")(v)}
              onSelectPlace={(p) => { setF((x) => ({ ...x, postcode: p.postcode, location: p.admin_district || x.location })); }}
              placeholder="EC2A 4NE"
              className="rounded-none h-12"
              testId="brief-postcode"
            />
          </Field>
        </div>
        <Field label="Approximate items *">
          <Input required value={f.approx_items} onChange={set("approx_items")} placeholder="60 desks, 240 chairs, 12 meeting tables" data-testid="brief-items" className="rounded-none h-12" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Move by date">
            <Input type="date" value={f.move_by_date} onChange={set("move_by_date")} data-testid="brief-date" className="rounded-none h-12" />
          </Field>
          <Field label="Budget (£, optional)">
            <Input type="number" min="0" step="50" value={f.budget} onChange={set("budget")} placeholder="e.g. 2500" data-testid="brief-budget" className="rounded-none h-12" />
          </Field>
        </div>
        <div className="pt-4 border-t border-border flex justify-end">
          <Button type="submit" disabled={submitting} data-testid="brief-submit" className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8">
            {submitting ? "Posting…" : "Post request →"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}
